import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import { useBooleanState } from "hooks";
import Popper from "components/Popper";

import { useEligibilityChecker } from "./hooks";
import { SCREEN_TYPE } from "../../Container";
import { useScreenManager } from "..";
import {
  InsightButtons,
  InsightsDisabledModal,
  InsightsView,
  PrivateEmbeddedInsight,
  TriggerButton,
} from "./components";

function InsightMenu({ insights, loading, user, onClick }) {
  const [menuOpen, openMenu, closeMenu] = useBooleanState();
  const [modalOpen, openUpgradeModal, closeUpgradeModal] = useBooleanState();
  const [insight, selectInsight] = useState();
  const screen = useScreenManager();
  const { checkInsights, isProcessing, error } = useEligibilityChecker(user);

  const isSSOEmbeddedInsight = useMemo(() => {
    return insights.some((r) => {
      return Boolean(r?.dashboard);
    });
  }, [insights]);

  if (insights.length < 1) return null;

  return (
    <React.Fragment>
      {menuOpen && isSSOEmbeddedInsight && (
        <div
          data-test="insight-menu-overlay"
          style={{
            position: "fixed",
            top: "calc(47px + 50px)",
            left: 0,
            width: "100vw",
            height: "calc(100vh - 47px - 50px)",
            backgroundColor: "rgba(0, 0, 0, 0)",
            zIndex: 100,
          }}
        />
      )}
      <div style={{ flex: "0 1 auto", margin: "0 0.5em 0 0" }}>
        <Popper
          data-test="insight-list-popup"
          style={{ padding: 0 }}
          position="top right"
          flowing
          open={menuOpen}
          trigger={
            <TriggerButton
              loading={loading || isProcessing}
              disabled={loading || isProcessing}
              isActive={Boolean(insight)}
              title={isSSOEmbeddedInsight ? "Insights" : "Reports"}
              onClick={() => {
                openMenu();
                if (
                  insight?.dashboard &&
                  screen.front() !== SCREEN_TYPE.INSIGHTS
                )
                  screen.promote(SCREEN_TYPE.INSIGHTS);
              }}
            />
          }
          onClose={(e) => {
            if (e.type !== "mouseleave") {
              closeMenu();
            }
          }}
        >
          <InsightButtons
            insights={insights}
            onClick={async (r) => {
              closeMenu();

              const success = await checkInsights();
              if (!success) {
                openUpgradeModal();
                return;
              }

              selectInsight(r);
              if (r?.dashboard) {
                onClick(r);

                if (screen.front() !== SCREEN_TYPE.INSIGHTS)
                  screen.promote(SCREEN_TYPE.INSIGHTS);
              }
            }}
          />
        </Popper>
      </div>
      {insight?.embedUrl && (
        <InsightsView.Modal
          onClose={() => {
            selectInsight(undefined);
          }}
        >
          <PrivateEmbeddedInsight
            embedUrl={insight.embedUrl}
            title={insight.name}
          />
        </InsightsView.Modal>
      )}
      {modalOpen && error && (
        <InsightsDisabledModal
          email={user.email}
          onClose={closeUpgradeModal}
          {...error}
        />
      )}
    </React.Fragment>
  );
}

InsightMenu.propTypes = {
  insights: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        embedUrl: PropTypes.string.isRequired,
      }),
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        dashboard: PropTypes.string.isRequired,
      }),
    ])
  ),
  loading: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    insightsEnabled: PropTypes.bool,
  }),
  onClick: PropTypes.func.isRequired,
};

InsightMenu.defaultProps = {
  insights: [],
  user: {},
  loading: false,
};

export default InsightMenu;
